import {
  isHalfOrFullWidthNumeric,
  FieldValidationResult,
  ValidationError,
} from './common-validation';
import { VALIDATION_MESSAGES, WITNESS_MESSAGES } from './validation-messages';

import type {
  WitnessCompanyRegistrationForm,
  NoharaRepresentative,
} from '../../types/company.types';

// Individual field validations
export const validateWitnessCompanyOfficialName = (name: string): FieldValidationResult => {
  if (!name.trim()) {
    return { isValid: false, error: WITNESS_MESSAGES.OFFICIAL_NAME_REQUIRED_SHORT };
  }
  return { isValid: true };
};

export const validateWitnessCompanyTel = (tel: string): FieldValidationResult => {
  if (!tel.trim()) {
    return { isValid: false, error: WITNESS_MESSAGES.TEL_REQUIRED };
  }
  if (!isHalfOrFullWidthNumeric(tel)) {
    return { isValid: false, error: WITNESS_MESSAGES.TEL_FORMAT };
  }
  return { isValid: true };
};

export const validateWitnessCompanyContactTel = (tel: string): FieldValidationResult => {
  if (!tel.trim() || tel === '' || tel === null) {
    return { isValid: true };
  }
  if (!isHalfOrFullWidthNumeric(tel)) {
    return { isValid: false, error: WITNESS_MESSAGES.CONTACT_TEL_FORMAT };
  }
  return { isValid: true };
};

export const validateWitnessCompanyFax = (fax: string): FieldValidationResult => {
  if (!fax.trim()) {
    return { isValid: false, error: WITNESS_MESSAGES.FAX_REQUIRED };
  }
  if (!isHalfOrFullWidthNumeric(fax)) {
    return { isValid: false, error: WITNESS_MESSAGES.FAX_FORMAT };
  }
  return { isValid: true };
};

export const validateWitnessCompanyRepresentatives = (
  representatives: NoharaRepresentative[]
): { isValid: boolean; errors: ValidationError[] } => {
  const errors: ValidationError[] = [];

  // Filter to only validate representatives that are visible/editable
  // Skip validation for records with canUpdate: false (they are hidden from UI)
  const visibleRepresentatives = representatives;

  if (visibleRepresentatives.length === 0) {
    errors.push({
      field: 'representatives',
      message: VALIDATION_MESSAGES.REPRESENTATIVES_MIN_REQUIRED,
    });
  } else {
    // Validate only visible representatives, but use original index for error field names
    for (const [index, rep] of representatives.entries()) {
      // Skip validation for records with canUpdate: false (they are hidden from UI)
      if (rep.canUpdate === false) {
        continue;
      }

      if (!rep.department.trim()) {
        errors.push({
          field: `representatives.${index}.department`,
          message: VALIDATION_MESSAGES.REPRESENTATIVE_DEPARTMENT_REQUIRED,
        });
      }
      if (!rep.salesPerson?.id.trim()) {
        errors.push({
          field: `representatives.${index}.salesPerson`,
          message: VALIDATION_MESSAGES.REPRESENTATIVE_SALES_PERSON_REQUIRED,
        });
      }
    }
  }

  return { isValid: errors.length === 0, errors };
};

// Main form validation
export const validateWitnessCompanyRegistrationForm = (
  formData: WitnessCompanyRegistrationForm
): { isValid: boolean; errors: ValidationError[] } => {
  const errors: ValidationError[] = [];

  const officialNameResult = validateWitnessCompanyOfficialName(formData.officialName);
  if (!officialNameResult.isValid) {
    errors.push({ field: 'officialName', message: officialNameResult.error });
  }

  const telResult = validateWitnessCompanyTel(formData.tel);
  if (!telResult.isValid) {
    errors.push({ field: 'tel', message: telResult.error });
  }

  const faxResult = validateWitnessCompanyFax(formData.fax);
  if (!faxResult.isValid) {
    errors.push({ field: 'fax', message: faxResult.error });
  }

  const contactTelResult = validateWitnessCompanyContactTel(formData?.contactTel ?? '');
  if (!contactTelResult.isValid) {
    errors.push({ field: 'contactTel', message: contactTelResult.error });
  }

  const representativesResult = validateWitnessCompanyRepresentatives(formData.representatives);
  if (!representativesResult.isValid) {
    errors.push(...representativesResult.errors);
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};
